import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPages, createPage } from '../../redux/Slices/pageSlice';
import { showError, getErrorMessage } from '../../utils/snackbarHelpers';
import PageListHeader from './PageListHeader';
import { TextSnippet, MoreHoriz, ChevronRight, Group } from '../icons';
import usePageSocket from '../../hooks/usePageSocket';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import SkeletonPageList from '../Skeletons/SkeletonPageList';
import MemberImage from '../Global/MemberImage';
import './scss/PageList.scss';

const PageList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  // fetchPages sets `listLoading` (not `loading`), so the list must read that —
  // otherwise the skeleton never shows and the empty state flashes before the
  // pages arrive.
  const { pages, listLoading, error } = useSelector((state) => state.pages);
  const { handlePopoverClick } = useHandlePopoverClick();

  const handleMoreClick = (e, page) => {
    e.preventDefault();
    e.stopPropagation();
    handlePopoverClick(e, 'pageActionsMenu', {
      page,
      workspaceId,
      isClosed: false
    });
  };

  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('lastOpened');
  const [filterOwner, setFilterOwner] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // Whether the first fetch for the current workspace has settled. Until then we
  // show the skeleton (not the empty state), so an unfetched list never flashes
  // "No pages found" before the data arrives.
  const [hasFetched, setHasFetched] = useState(false);

  // Listen for real-time page updates
  usePageSocket(workspaceId);

  useEffect(() => {
    setHasFetched(false);
    dispatch(fetchPages({
      workspaceId,
      archived: showArchived,
      filter: filterOwner,
      sortBy
    })).finally(() => setHasFetched(true));
  }, [dispatch, workspaceId, showArchived, filterOwner, sortBy]);

  const handleCreatePage = async () => {
    if (isCreating) return; // guard against double-submit
    setIsCreating(true);
    try {
      const result = await dispatch(createPage({
        title: 'Untitled',
        workspaceId,
        type: workspaceId ? 'workspace' : 'personal'
      })).unwrap();
      navigate(`/p/${result.shortId}`);
    } catch (err) {
      console.error('Failed to create page:', err);
      showError(dispatch, getErrorMessage(err, "Couldn't create the page. Please try again."));
    } finally {
      setIsCreating(false);
    }
  };

  const filteredPages = (pages || []).filter(page =>
    page.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const recentPages = [...filteredPages]
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 12);

  const scrollContainerRef = React.useRef(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScrollEvent = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftShadow(scrollLeft > 0);
      // Use a small buffer (1px) to avoid rounding issues
      setShowRightShadow(scrollLeft + clientWidth < scrollWidth - 1);
    };

    container.addEventListener('scroll', handleScrollEvent);
    window.addEventListener('resize', handleScrollEvent);

    // Initial check
    handleScrollEvent();

    // ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(() => {
      handleScrollEvent();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScrollEvent);
      window.removeEventListener('resize', handleScrollEvent);
      resizeObserver.disconnect();
    };
  }, [recentPages]); // Re-run when pages change as content might shift

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 600;
      const container = scrollContainerRef.current;
      const newScrollLeft = direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };



  const getDisplayMembers = (page) => {
    // robustly filter members to ensure they are populated user objects
    const members = page.members?.map(m => m.user).filter(u => u && typeof u === 'object' && u._id) || [];
    if (members.length > 0) return members;

    // Fallback to owner if populated
    if (page.ownerUserId && typeof page.ownerUserId === 'object' && page.ownerUserId._id) {
      return [page.ownerUserId];
    }
    return [];
  };

  return (
    <div className="page-list-container-v2">
      <PageListHeader
        viewMode={viewMode}
        setViewMode={setViewMode}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterOwner={filterOwner}
        setFilterOwner={setFilterOwner}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onCreatePage={handleCreatePage}
        isCreating={isCreating}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
      />

      <div className="page-list-content">
        {(listLoading || !hasFetched) && pages.length === 0 ? (
          <SkeletonPageList />
        ) : (
          <>
            {error && <div className="page-list-error">{error}</div>}

            {!searchTerm && !showArchived && recentPages.length > 0 && (
              <div className="recent-section">
                <h2 className="section-title">Recently visited</h2>

                <div className={`carousel-container ${showLeftShadow ? 'show-left-shadow' : ''} ${showRightShadow ? 'show-right-shadow' : ''}`}>
                  {showLeftShadow && (
                    <button
                      className="carousel-btn left"
                      onClick={() => handleScroll('left')}
                      aria-label="Scroll left"
                    >
                      <ChevronRight />
                    </button>
                  )}

                  <div
                    className="recent-carousel"
                    ref={scrollContainerRef}
                  >
                    {recentPages.map(page => (
                      <div
                        key={page._id}
                        className="recent-card"
                        onClick={() => navigate(`/p/${page.shortId}`)}
                      >
                        <div className="recent-card-cover">
                          {page?.coverImage?.url ? (
                            <img src={page?.coverImage?.url} alt={page.title} className="cover-img" loading="lazy" />
                          ) : (
                            <div className="cover-placeholder" />
                          )}
                          <button
                            id={`recent-page-actions-btn-${page._id}`}
                            className="card-actions-btn"
                            onClick={(e) => handleMoreClick(e, page)}
                            data-popover-trigger
                          >
                            <MoreHoriz />
                          </button>
                        </div>

                        <div className="recent-card-content">
                          <div className="recent-card-icon">
                            {page.icon || '📄'}
                          </div>

                          <div className="card-title-rows">
                            <div className="card-title">{page.title || 'Untitled'}</div>
                          </div>

                          <div className="recent-card-footer">
                            <div className="footer-left">
                              <span className="date-text">
                                {new Date(page.updatedAt).toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>

                            <div className="footer-right">
                              <div
                                className="members-info-trigger"
                                id={`popover-recent-view-more-members-${page.shortId}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handlePopoverClick(e, 'moreMembers', { boardId: page.shortId, type: 'page', isBack: true, backPopover: 'moreMembers' });
                                }}
                                data-popover-trigger
                                data-tooltip-content="Members"
                                data-tooltip-position="top"
                              >
                                <Group size={20} color="var(--secondary-text-color)" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {showRightShadow && (
                    <button
                      className="carousel-btn right"
                      onClick={() => handleScroll('right')}
                      aria-label="Scroll right"
                    >
                      <ChevronRight />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="all-pages-section">
              <div className="section-header">
                <h2 className="section-title">
                  {searchTerm ? `Search results for "${searchTerm}"` : (showArchived ? 'Archived Pages' : 'All Pages')}
                </h2>
                <span className="page-count">{filteredPages.length} documents</span>
              </div>

              {filteredPages.length > 0 ? (
                <div className={`pages-display ${viewMode}`}>
                  {/* Table Header for List View */}
                  {viewMode === 'list' && (
                    <div className="list-table-header">
                      <div className="col-name">Name</div>
                      <div className="col-location">Location</div>
                      <div className="col-date">Last Edited</div>
                    </div>
                  )}

                  {filteredPages.map(page => (
                    <div
                      key={page._id}
                      className={`page-item-card ${viewMode}`}
                      onClick={() => navigate(`/p/${page.shortId}`)}
                    >
                      {viewMode === 'grid' ? (
                        <>
                          <div className="card-cover">
                            {page?.coverImage?.url ? (
                              <img src={page?.coverImage?.url} alt={page.title} className="cover-img" loading="lazy" />
                            ) : (
                              <div className="cover-placeholder" />
                            )}
                            <button
                              id={`page-actions-btn-${page._id}`}
                              className="card-actions-btn"
                              onClick={(e) => handleMoreClick(e, page)}
                              data-popover-trigger
                            >
                              <MoreHoriz />
                            </button>
                          </div>
                          <div className="card-content">
                            <div className="card-icon">{page.icon || '📄'}</div>
                            <h3 className="card-title">{page.title || 'Untitled'}</h3>
                            <div className="card-footer">
                              {/* {page.workspaceId ? (
                            <span className="workspace-name">{page.workspaceId.name}</span>
                          ) : (
                            <span className="workspace-name personal">Personal</span>
                          )} */}

                              <div className="footer-right">
                                <span className="date-text">
                                  {new Date(page.updatedAt).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>

                                <div
                                  className="members-info-trigger"
                                  id={`popover-more-members-${page.shortId}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePopoverClick(e, 'moreMembers', { boardId: page.shortId, type: 'page', isBack: true, backPopover: 'moreMembers' });
                                  }}
                                  data-popover-trigger
                                  data-tooltip-content="Members"
                                  data-tooltip-position="top"
                                >
                                  <Group size={20} color="var(--secondary-text-color)" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="page-main-info">
                            <div className="page-icon-wrapper">{page.icon || '📄'}</div>
                            <div className="page-title">{page.title || 'Untitled'}</div>
                          </div>

                          <div className="page-meta-data">
                            <div className="page-location">
                              {page.workspaceId ? (
                                <span className="loc-badge workspace">{page.workspaceId.name || 'Workspace'}</span>
                              ) : (
                                <span className="loc-badge personal">Personal</span>
                              )}
                            </div>
                            <div className="page-date">
                              {new Date(page.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (hasFetched && !listLoading) ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <TextSnippet size={48} />
                  </div>
                  <h3>No pages found</h3>
                  <p>Create a new page to get started or adjust your filters</p>
                  <button className="create-empty-btn" onClick={handleCreatePage} disabled={isCreating}>
                    {isCreating ? 'Creating…' : 'Create New Page'}
                  </button>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PageList;
